import { useState } from "react";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  BadgeCheck, Loader2, CheckCircle2, XCircle, RefreshCw, ExternalLink,
  Zap, ArrowRight, Clock, ShieldCheck,
} from "lucide-react";

const STEPS_META = [
  { step: 1, title: "GET /homologacao/produtos", desc: "Obtém dados do produto e o primeiro hash de encadeamento." },
  { step: 2, title: "POST /homologacao/produtos", desc: "Cria o produto com os dados obtidos no passo 1 + hash." },
  { step: 3, title: "PUT /homologacao/produtos/{id}", desc: "Atualiza nome do produto para 'Copo'." },
  { step: 4, title: "PATCH /homologacao/produtos/{id}/situacoes", desc: "Altera situação para 'I' (inativo)." },
  { step: 5, title: "DELETE /homologacao/produtos/{id}", desc: "Remove o produto criado nos testes." },
];

export default function BlingHomologacao() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/erp/bling/homologacao/run");
      setResult(data);
      if (data.ok) {
        toast.success(`Homologação concluída em ${data.elapsed_ms}ms — todos os 5 passos passaram!`);
      } else {
        toast.error(`Homologação falhou no passo ${data.aborted_at || "?"}. Veja o detalhamento abaixo.`, { duration: 8000 });
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Erro ao executar homologação: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`, { duration: 12000 });
    } finally {
      setRunning(false);
    }
  };

  const stepsMap = (result?.steps || []).reduce((acc, s) => {
    acc[s.step] = s;
    return acc;
  }, {});

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 sm:p-10">
        <header className="mb-8">
          <div className="eyebrow mb-2 flex items-center gap-2">
            <BadgeCheck className="w-4 h-4 text-[#002FA7]" /> Compliance Bling
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B] mb-2">
            Homologação do App Bling
          </h1>
          <p className="text-sm text-zinc-500 max-w-2xl">
            Executa a sequência de 5 requests obrigatórios da{" "}
            <a
              href="https://developer.bling.com.br/homologacao#execu%C3%A7%C3%A3o"
              target="_blank"
              rel="noreferrer"
              className="text-[#002FA7] underline"
            >
              documentação oficial do Bling
            </a>
            . O access token será invalidado durante o teste (comportamento intencional do Bling) — o refresh automático via refresh_token está tratado.
          </p>
        </header>

        <div className="bg-white border border-zinc-200 rounded-md p-6 mb-6 max-w-3xl">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <h2 className="font-display font-semibold text-[#09090B] text-lg mb-1">
                Executar homologação
              </h2>
              <p className="text-xs text-zinc-500">
                Tempo total ≤ 10 segundos · limite entre requests 2 segundos · hash de encadeamento repassado no header <code className="bg-zinc-100 px-1 rounded font-mono">x-bling-homologacao</code>.
              </p>
            </div>
            <Button
              onClick={run}
              disabled={running}
              className="brand-primary text-white rounded-md h-10 px-5"
              data-testid="bling-homolog-run"
            >
              {running ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Executando…
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" /> Rodar teste
                </>
              )}
            </Button>
          </div>

          {result && (
            <div
              className={`border rounded-md p-4 mb-4 flex items-start gap-3 ${
                result.ok
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-red-200 bg-red-50"
              }`}
              data-testid="bling-homolog-summary"
            >
              {result.ok ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <div className={`font-display font-semibold text-sm mb-1 ${result.ok ? "text-emerald-900" : "text-red-900"}`}>
                  {result.ok
                    ? "Todos os 5 passos passaram · pronto para solicitar revisão no painel do Bling"
                    : `Homologação falhou no passo ${result.aborted_at || "?"}`}
                </div>
                <div className="text-xs font-mono flex items-center gap-4 flex-wrap">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {result.elapsed_ms}ms
                    {result.elapsed_ms > 10000 && <span className="text-red-700 ml-1">(> limite 10s)</span>}
                  </span>
                  {result.token_refreshed && (
                    <span className="flex items-center gap-1 text-blue-700">
                      <RefreshCw className="w-3 h-3" /> Refresh token acionado
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Steps table */}
        <div className="space-y-3 max-w-3xl">
          {STEPS_META.map((meta) => {
            const s = stepsMap[meta.step];
            const state = !result
              ? "idle"
              : !s
              ? "skipped"
              : s.ok
              ? "ok"
              : "fail";
            return (
              <div
                key={meta.step}
                className={`border rounded-md p-4 bg-white ${
                  state === "ok"
                    ? "border-emerald-200"
                    : state === "fail"
                    ? "border-red-300"
                    : "border-zinc-200"
                }`}
                data-testid={`bling-homolog-step-${meta.step}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 font-mono font-bold text-sm ${
                      state === "ok"
                        ? "bg-emerald-100 text-emerald-800"
                        : state === "fail"
                        ? "bg-red-100 text-red-800"
                        : "bg-zinc-100 text-zinc-600"
                    }`}
                  >
                    {state === "ok" ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : state === "fail" ? (
                      <XCircle className="w-4 h-4" />
                    ) : (
                      meta.step
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm font-semibold text-[#09090B]">
                      Passo {meta.step} · {meta.title}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{meta.desc}</div>
                    {s && (
                      <div className="mt-2 space-y-1">
                        <div className="text-[11px] font-mono text-zinc-600 flex items-center gap-3 flex-wrap">
                          <span>HTTP {s.status_code}</span>
                          {s.hash_returned && (
                            <span className="flex items-center gap-1">
                              <ArrowRight className="w-3 h-3" />
                              hash: <code className="bg-zinc-100 px-1 rounded">{s.hash_returned}</code>
                            </span>
                          )}
                          {s.product_id && <span>id: {s.product_id}</span>}
                          {s.token_refreshed && (
                            <span className="text-blue-700 flex items-center gap-1">
                              <RefreshCw className="w-3 h-3" /> após refresh
                            </span>
                          )}
                        </div>
                        {s.error && (
                          <div className="text-[11px] font-mono text-red-700 bg-red-50 rounded p-2 break-words">
                            {s.error}
                          </div>
                        )}
                        {s.response_preview && !s.error && (
                          <div className="text-[10px] font-mono text-zinc-500 truncate">
                            {s.response_preview}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 max-w-3xl">
          <div className="border border-zinc-200 bg-white rounded-md p-5 flex items-start gap-4">
            <ShieldCheck className="w-6 h-6 text-emerald-700 flex-shrink-0 mt-1" />
            <div className="text-sm text-zinc-700 leading-relaxed">
              <div className="font-semibold text-[#09090B] mb-1">
                Depois que todos os 5 passos passarem
              </div>
              <ol className="list-decimal ml-5 space-y-1 text-xs text-zinc-600">
                <li>Volte no painel do Bling → <strong>Central de Extensões → seu App → Homologação</strong></li>
                <li>Clique em <strong>"Iniciar revisão"</strong> — Bling refará os mesmos 5 testes contra o seu app</li>
                <li>Se passar, o botão <strong>"Solicitar revisão"</strong> fica disponível</li>
                <li>Complete os campos do App (logo, descrição, link do manual, vídeo, escopos etc.) conforme a{" "}
                  <a
                    href="https://developer.bling.com.br/homologacao"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#002FA7] underline"
                  >
                    documentação
                  </a>
                </li>
                <li>Envie para revisão — a equipe técnica do Bling responde em 1-2 dias úteis</li>
              </ol>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
